import { create } from 'zustand';
import { useWindowStore } from './window-store';
import { isVisible } from './window-rules';
import { isUiRefactoringOn } from '~/lib/ui-refactoring';

/**
 * FRONT-3003: whether the Pro Builder window is showing. Two separate facts on
 * purpose. `open` is only visibility — closing HIDES the window, it never
 * unmounts it, because the terminal inside it is a running Claude Code.
 * `everOpened` is the mount latch: the app shell mounts the window only after
 * the first open request, so nobody who never opens Pro Builder gets a
 * terminal session, and it stays mounted afterwards (until the tab closes).
 *
 * FRONT-3146: Pro Builder is one of the shared windows (`window-store.ts`,
 * id `pro-builder`), so it can sit beside one other window, be minimized into
 * the window bar, or wait for the "which window to minimize?" choice. This
 * store keeps its public API for its many callers and routes through the window
 * store; `open` is MIRRORED from it (visible there = open here), so every
 * existing reader of `open` keeps meaning "the person can see it".
 */
interface ProBuilderOpenState {
  open: boolean;
  everOpened: boolean;
  openProBuilder: () => void;
  closeProBuilder: () => void;
}

/** The window id Pro Builder has in the window store. */
const PRO_BUILDER_ID = 'pro-builder';

/**
 * The window's open EDGE (hidden -> shown), which the remaining full-screen
 * overlays (the desktop operator panel, Settings) close on
 * (`use-close-on-pro-builder-open.ts`). Not "is open": a surface that mounts
 * while the window is already open reads that as the state it found, never as
 * the window opening over it — the caller seeds `previous` with the mount-time
 * value, the same rule the operator-panel request watcher uses.
 */
export function isProBuilderOpenEdge(previous: boolean, next: boolean): boolean {
  return !previous && next;
}

export const useProBuilderOpenStore = create<ProBuilderOpenState>((set) => ({
  open: false,
  everOpened: false,
  openProBuilder: () => {
    // FRONT-3223: no window layer while `eddy.ui-refactoring` is off — plain open/close,
    // and the window store stays empty so the top bar shows no tab for it.
    if (!isUiRefactoringOn()) {
      set({ open: true, everOpened: true });
      return;
    }
    useWindowStore.getState().open({ id: PRO_BUILDER_ID, kind: 'pro-builder', title: 'Pro Builder' }, () => {
      // Dropped from the chooser's Cancel: it was never shown.
      set({ open: false });
    });
    set({ open: isVisible(useWindowStore.getState(), PRO_BUILDER_ID), everOpened: true });
  },
  closeProBuilder: () => {
    if (isUiRefactoringOn()) useWindowStore.getState().close(PRO_BUILDER_ID);
    set({ open: false });
  },
}));

// Minimize, restore, the chooser and a window-bar close all change visibility
// without going through this store's actions — keep `open` in step.
useWindowStore.subscribe((state) => {
  if (!isUiRefactoringOn()) return;
  const visible = isVisible(state, PRO_BUILDER_ID);
  if (useProBuilderOpenStore.getState().open !== visible) useProBuilderOpenStore.setState({ open: visible });
});
